import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Images } from "../../../utils/imageConstants";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import { Api } from "../../../services/interceptor";
import { getPlayerImageUrl } from "../../../helper/helper";
import { colorFor } from "../../tournamentPage/matchVm";
import type { ILeagueStats, ITournamentPlayerStats } from "../types/home";

type Col = { label: string; key: string; main?: boolean };
type BoardDef = { key: string; matchKeys: string[]; title: string; icon: React.ReactNode; cols: Col[]; rankBy: string };

const IcTrS = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M7 4h10v5a5 5 0 01-10 0V4zM7 6H4v1.5A3.5 3.5 0 007.5 11M17 6h3v1.5a3.5 3.5 0 01-3.5 3.5M12 14v3.5M8.5 20h7M9.5 17.5h5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>);

/** Most valuable player, Runs and Wickets: the top 5 across every tournament in the league. */
const BOARDS: BoardDef[] = [
  { key: "mvp", matchKeys: ["Most Valuable Player"], title: "Most valuable player", icon: <IcTrS />, cols: [{ label: "Points", key: "Points", main: true }], rankBy: "Points" },
  { key: "runs", matchKeys: ["Runs"], title: "Runs", icon: <img src={Images.batting_record_icon} alt="" />, cols: [{ label: "Mat", key: "Mat" }, { label: "Avg", key: "Avg" }, { label: "Runs", key: "Runs", main: true }], rankBy: "Runs" },
  { key: "wickets", matchKeys: ["Wickets"], title: "Wickets", icon: <img src={Images.most_wickets_icon} alt="" />, cols: [{ label: "Mat", key: "Mat" }, { label: "Wkts", key: "Wkts", main: true }], rankBy: "Wkts" },
];

const statOf = (p: ITournamentPlayerStats, key: string) => p.statsList?.find((s) => s.key?.toLowerCase() === key.toLowerCase())?.value;
const initials = (p: ITournamentPlayerStats) => `${p.playerResponse?.playerFirstName?.[0] ?? ""}${p.playerResponse?.playerLastName?.[0] ?? ""}`.toUpperCase();

/** Ties share a rank ("=2"). */
const ranked = (list: ITournamentPlayerStats[], key: string) => {
  const v = list.map((p) => Number(statOf(p, key)));
  return list.map((p, i) => {
    let rk = i + 1;
    while (rk > 1 && v[rk - 2] === v[i] && !Number.isNaN(v[i])) rk--;
    return { p, rk, eq: v.filter((x) => x === v[i] && !Number.isNaN(x)).length > 1 };
  });
};

const Board = ({ b, data }: { b: BoardDef; data?: ILeagueStats }) => {
  const rows = ranked(data?.tournamentPlayerStats ?? [], b.rankBy);
  const n = b.cols.length;
  return (
    <div className="ldb alb">
      <div className="lbh"><span className="lic">{b.icon}</span><h3>{b.title}</h3><span className="q">All time</span></div>
      {rows.length > 0 && <div className="alh" style={{ ["--n" as string]: n } as React.CSSProperties}><span /><span className="pl">Player</span>{b.cols.map((c) => <span key={c.key}>{c.label}</span>)}</div>}
      {rows.length ? rows.map(({ p, rk, eq }, i) => {
        const team = p.teamResponse?.teamName ?? "";
        const color = colorFor(team);
        const img = p.playerResponse?.imageUri ? getPlayerImageUrl(p.playerResponse.imageUri) : null;
        return (
          <div key={`${p.playerResponse?.playerId}-${i}`} className={`alr ${i === 0 ? "first" : ""}`} style={{ ["--n" as string]: n } as React.CSSProperties}>
            <span className="rk">{eq ? "=" : ""}{rk}</span>
            <span className="av" style={{ background: `${color}1F`, color, overflow: "hidden" }}>{img ? <img src={img} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : initials(p)}</span>
            <span className="nm"><b>{p.playerResponse?.playerFirstName} {p.playerResponse?.playerLastName}</b><span>{team}</span></span>
            {b.cols.map((c) => <span key={c.key} className={`c ${c.main ? "v" : ""}`}>{statOf(p, c.key) ?? "–"}</span>)}
          </div>
        );
      }) : <div className="lbe">Leaders appear here after the league's first match.</div>}
    </div>
  );
};

const LeagueStats = ({ leagueId }: { leagueId?: string | null }) => {
  const { isLoading, data } = useQuery<ILeagueStats[]>({
    queryKey: [QUERY_KEY.getLeagueStats, leagueId],
    enabled: !!leagueId,
    queryFn: async () => {
      const response = await Api.get(QUERY_KEY.getLeagueStats, { params: { leagueId, topN: "5" } });
      return Array.isArray(response?.data) ? response.data : [];
    },
  });
  const find = (b: BoardDef) => data?.find((s) => b.matchKeys.some((k) => k.toLowerCase() === s.key?.trim().toLowerCase()));
  const any = BOARDS.some((b) => (find(b)?.tournamentPlayerStats?.length ?? 0) > 0);

  return (
    <section className="lsec" id="secS" aria-labelledby="hS">
      <div className="lsh"><h2 id="hS">All-time league stats</h2><span className="sub">Top 5</span></div>
      {isLoading ? (
        <div className="lbgrid">{[0, 1, 2].map((i) => (
          <div key={i} className="skc" style={{ padding: 18, gap: 14 }}><span className="sk" style={{ height: 14, width: "50%" }} />{[0, 1, 2, 3, 4].map((j) => <span key={j} className="sk" style={{ height: 38 }} />)}</div>
        ))}</div>
      ) : (
        <>
          <div className="lbgrid">{BOARDS.map((b) => <Board key={b.key} b={b} data={find(b)} />)}</div>
          {any && <div className="skey"><b>Across every tournament in the league.</b> Players are ranked by the last column; tied players share a rank (=).</div>}
        </>
      )}
    </section>
  );
};

export default LeagueStats;
